import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import {
  STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE,
  STAGE_1_APPROVE_VALUE,
  STAGE_2_APPROVE_VALUE,
  STAGE_APPROVED_VALUE,
} from "../../../constants/app";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import { useAppSelector, useAppSelector as useSelector } from "../../../store";
import {
  TCandidateProfileStesps,
  TCompletionStatus,
} from "../../../store/onboarding/types";
import { computeMissingDocs } from "../../../utils/missingDocsHelper";
import ProgressBar from "../../UI/ProgressBar";
import "../MyProfile/MyProfile.scss";
import StageNotificationBanner from "./StageNotificationBanner";

const stage1Keys = [
  "id_verification",
  "personal_details",
  "qualification",
  "jobpreference",
  "online_interview",
];
const stage2Keys = [
  "working_with_children",
  "police_check",
  "right_to_work",
  "reference_check",
];
const stage3Keys = [
  "tfn_form",
  "superannuation_form",
  "contract_form",
  "casual_fair_work_statement", 
  "bankAccount",
  "emergency_info",
];

// Section name mappings from API to display names
const sectionDisplayNames: Record<string, string> = {
  id_verification: "ID Verification",
  online_interview: "Online Interview",
  personal_details: "Personal Details",
  qualification: "Qualifications",
  jobpreference: "Job Preferences",
  working_with_children: "Working With Children",
  police_check: "Police Check",
  right_to_work: "Right To Work",
  reference_check: "Reference Check",
  tfn_form: "TFN Declaration",
  superannuation_form: "Superannuation",
  contract_form: "Contract",
  casual_fair_work_statement: "Casual & Fair Work Statement",
  bankAccount: "Bank Account",
  emergency_info: "Next of Kin",
};

// Section name to route mappings
const sectionRouteMap: Record<string, string> = {
  id_verification: "id-verification",
  online_interview: "online-interview",
  personal_details: "personal-details",
  qualification: "qualifications",
  jobpreference: "job-preferences",
  working_with_children: "complaince-check",
  police_check: "complaince-check",
  right_to_work: "complaince-check",
  reference_check: "complaince-check",
  tfn_form: "tfn-declaration",
  superannuation_form: "superannuation",
  contract_form: "contract-form",
  casual_fair_work_statement: "casual-and-fair-work-statement",
  bankAccount: "bank-account",
  emergency_info: "next-of-kin",
};

const MyProfileReopened: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeStage, setActiveStage] = useState<string | null>("");
  const [currentStageName, setCurrentStageName] = useState<string | null>("");
  const [navigateTo, setNavigateTo] = useState<string>("");
  const [stage1CompletionPercentage, setStage1CompletionPercentage] = useState<string | null>('0');
  const [stage2CompletionPercentage, setStage2CompletionPercentage] = useState<string | null>('0');
  const [stage3CompletionPercentage, setStage3CompletionPercentage] = useState<string | null>('0');
  const [missingDocs, setMissingDocs] = useState<string[]>([]);

  const reopened_stages_status = useAppSelector((state) => state.onboarding.reopenedStagesStatus);

  // const isAdminCompleted = reopened_stages_status?.find((reopenData) =>reopenData?.stage_number === 2 && reopenData?.sections?.includes("admin_complete") && (reopenData?.status === "approved"))

  const stepCompletion: TCompletionStatus | null = useSelector(
    (state) => state.onboarding.completedstatus
  );

  const candidateProfileStages = useAppSelector((state) => state.onboarding.candidateProfileSteps)
  const profileData = useAppSelector((state) => state.onboarding.personaldetails)
  const userpersonaldetails = useSelector((state) => state.onboarding.userpersonaldetails)
  const qualifications = useSelector((state) => state.onboarding.qualifications);
  const personaldetails = useSelector(
    (state) => state.onboarding.personaldetails
  );

  const currentStage = profileData?.current_stage ? profileData?.current_stage : null;

  // Compute the effective stage the candidate should be working on based on approval status.
  // Rules:
  //   current_stage 1 + approved  → 2  |  current_stage 1 + not approved → 1
  //   current_stage 2 + approved  → 3  |  current_stage 2 + not approved → 2
  //   current_stage 3 (or higher) → keep as-is
  const effectiveCurrentStage = useMemo(() => {
    if (!currentStage) return 1;
    if (currentStage === 1) {
      const stage1Data = candidateProfileStages?.find(
        (stage: TCandidateProfileStesps) => stage.step_number === 1
      );
      const stage1Approved =
        stage1Data &&
        (stage1Data.action === STAGE_1_APPROVE_VALUE ||
          stage1Data.action === STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE);
      return stage1Approved ? 2 : 1;
    } else if (currentStage === 2) {
      const stage2Data = candidateProfileStages?.find(
        (stage: TCandidateProfileStesps) => stage.step_number === 2
      );
      const stage2Approved = stage2Data && stage2Data.action === STAGE_2_APPROVE_VALUE;
      return stage2Approved ? 3 : 2;
    }
    return currentStage;
  }, [currentStage, candidateProfileStages]);

  // Use custom hook for reopened stages logic
  const {
    reopenedStageNumbers,
    reopenedSections,
    isStageReopenedFn,
    isSectionReopenedFn,
  } = useReopenedStages();

  const stage1Values = useMemo(() => {
    return stepCompletion
      ? Object.keys(stepCompletion)
          .filter((key) => stage1Keys.includes(key))
          .map((key) => stepCompletion[key as keyof TCompletionStatus])
      : [];
  }, [stepCompletion]);

  const stage2Values = useMemo(() => {
    return stepCompletion
      ? Object.keys(stepCompletion)
          .filter((key) => stage2Keys.includes(key))
          .map((key) => stepCompletion[key as keyof TCompletionStatus])
      : [];
  }, [stepCompletion]);

  const stage3Values = useMemo(() => {
    return stepCompletion
      ? Object.keys(stepCompletion)
          .filter((key) => stage3Keys.includes(key))
          .map((key) => stepCompletion[key as keyof TCompletionStatus])
      : [];
  }, [stepCompletion]);

   const isPersonalDetailsHaveResume = useMemo(() => {
    if(personaldetails?.resume === null || personaldetails === null) {
      return true;
    }
    return false;
  }, [personaldetails]);

  const checkStageSubmittedOrNot = useCallback((stageNumber: number | null) => {
    if(currentStage === stageNumber){
      return true;
    } else{
      return false;
    } 
  }, [currentStage]);

  const isStageOneReopened = useCallback(() => {
    return isStageReopenedFn(1);
  }, [isStageReopenedFn]);

  const isStageTwoReopened = useCallback(() => {
    return isStageReopenedFn(2);
  }, [isStageReopenedFn]);

  const isStageThreeReopened = useCallback(() => {
    return isStageReopenedFn(3);
  }, [isStageReopenedFn]);

  // Get reopened sections for Stage 1
  const stage1ReopenedSections = useMemo(() => {
    return stage1Keys.filter(key => reopenedSections.includes(key));
  }, [reopenedSections]);

  // Get reopened sections for Stage 3
  const stage3ReopenedSections = useMemo(() => {
    return stage3Keys.filter(key => reopenedSections.includes(key));
  }, [reopenedSections]);

  const calculateStageCompletionPercentage = useCallback((stageId: string | null) => {
    let result: string = "0";
    if (stageId === "stage1") {
      // Only calculate based on reopened sections
      const totalSteps = stage1ReopenedSections.length > 0 ? stage1ReopenedSections.length : 5;
      
      if (stage1ReopenedSections.length > 0) {
        // Count completed reopened sections only
        const completedSteps = stage1ReopenedSections.filter(key => 
          stepCompletion?.[key as keyof TCompletionStatus] === 1
        ).length;
        result = String((completedSteps / totalSteps) * 100);
      } else {
        // Fallback to original logic if no reopened sections
        const profileCompletedStatus = stage1Values;
        let completedSteps = Object.values(profileCompletedStatus).reduce(
          (acc: number, curr: number) => acc + curr,
          0
        );
        
        if (isPersonalDetailsHaveResume && stepCompletion?.personal_details === 1) {
          completedSteps = completedSteps - 1;
        }

        if(stepCompletion?.qualification === 1 && missingDocs.length !== 0) {
          completedSteps = completedSteps - 1;
        }

        result = String((completedSteps / totalSteps) * 100);
      }
      setStage1CompletionPercentage(result);
    } else if (stageId === "stage2") {
      const totalSteps: number = 4;
      const referroCompletedStatus = stage2Values;
      const completedSteps: any = Object.values(referroCompletedStatus).reduce(
        (acc: number, curr: any) => acc + curr,
        0
      );
      result = String((completedSteps / totalSteps) * 100);
      if(isStageTwoReopened() && !isAdminCompleted && userpersonaldetails?.referoo_status !== "pending"){
        result = "0";
      } else if(userpersonaldetails?.referoo_status === "pending" || userpersonaldetails?.referoo_status === "in_progress"){
        result = "50";
      }else if(userpersonaldetails?.referoo_status === "complete"){
        result = "100";
      }
      setStage2CompletionPercentage(result);
    } else if (stageId === "stage3") {
      // Only calculate based on reopened sections
      const totalSteps = stage3ReopenedSections.length > 0 ? stage3ReopenedSections.length : 6;
      
      if (stage3ReopenedSections.length > 0) {
        // Count completed reopened sections only
        const completedSteps = stage3ReopenedSections.filter(key => 
          stepCompletion?.[key as keyof TCompletionStatus] === 1
        ).length;
        result = String(Math.round((completedSteps / totalSteps) * 100));
      } else {
        // Fallback to original logic
        const formsCompletedStatus = stage3Values;
        const completedSteps = Object.values(formsCompletedStatus).reduce(
          (acc: number, curr: number) => acc + curr,
          0
        );
        result = String(Math.round((completedSteps / totalSteps) * 100));
      }
      setStage3CompletionPercentage(result);
    }
  }, [stage1Values, stage2Values, stage3Values, stepCompletion, userpersonaldetails, isPersonalDetailsHaveResume, missingDocs.length, stage1ReopenedSections, stage3ReopenedSections]);

  // Determine initial active stage based on reopened stages
  useEffect(() => {
    if (reopenedStageNumbers.length > 0) {
      // Set the first reopened stage as active
      const firstReopenedStage = Math.min(...reopenedStageNumbers);
      setActiveStage(`stage${firstReopenedStage}`);
    } else {
      setActiveStage('stage1');
    }
  }, [reopenedStageNumbers]);

  // Navigate to appropriate section on mount or when active stage changes
  useEffect(() => {
    if(activeStage === 'stage1' && (isStageOneReopened() || effectiveCurrentStage === 1)){
      // Find first incomplete section in reopened sections for stage 1
      const stage1ReopenedSections = reopenedSections.filter(section => stage1Keys.includes(section));

      // If already on a valid reopened section route (any stage), skip auto-navigation
      const isOnReopenedSection = reopenedSections.some(
        key => sectionRouteMap[key] && location.pathname.includes(sectionRouteMap[key])
      );

      if (!isOnReopenedSection) {
        if (stage1ReopenedSections.length > 0) {
          const firstIncomplete = stage1ReopenedSections.find(key => stepCompletion?.[key as keyof TCompletionStatus] !== 1);
          const route = firstIncomplete ? sectionRouteMap[firstIncomplete] : undefined;
          const firstReopenedRoute = sectionRouteMap[stage1ReopenedSections[0]];
          if (route && navigateTo === "") {
            navigate(`/candidate/my-profile/${route}`);
          } else {
            navigateTo === "" && navigate(`/candidate/my-profile/${firstReopenedRoute}`);
          }
        } else {
          navigateTo === "" && navigate('/candidate/my-profile/id-verification');
        }
      }
    } else if(activeStage === 'stage2' && (isStageTwoReopened() || effectiveCurrentStage === 2)){
       navigateTo === "" && navigate('/candidate/my-profile/complaince-check');
    } else if(activeStage === 'stage3' && (isStageThreeReopened() || effectiveCurrentStage === 3)){
      // Find first incomplete section in reopened sections for stage 3
      const stage3ReopenedSections = reopenedSections.filter(section => stage3Keys.includes(section));

      // If already on a valid reopened section route, skip auto-navigation
      // (prevents re-navigating back to TFN when handleNext has moved the user forward)
      const isOnReopenedSection = stage3ReopenedSections.some(
        key => location.pathname.includes(sectionRouteMap[key])
      );

      if (!isOnReopenedSection) {
        if (stage3ReopenedSections.length > 0) {
          const firstIncomplete = stage3ReopenedSections.find(key => stepCompletion?.[key as keyof TCompletionStatus] !== 1);
          const route = firstIncomplete ? sectionRouteMap[firstIncomplete] : undefined;
          if (route && navigateTo === "") {
            navigate(`/candidate/my-profile/${route}`);
          } else {
            navigateTo === "" && navigate('/candidate/my-profile/tfn-declaration');
          }
        } else {
          if(stepCompletion?.tfn_form === 0){
            navigate('/candidate/my-profile/tfn-declaration');
          } else if(stepCompletion?.superannuation_form === 0){
            navigate('/candidate/my-profile/superannuation');
          } else if(stepCompletion?.contract_form === 0){
            navigate('/candidate/my-profile/contract-form');
          } else if(stepCompletion?.casual_fair_work_statement === 0){
            navigate('/candidate/my-profile/casual-and-fair-work-statement');
          } else if(stepCompletion?.bankAccount === 0){
            navigate('/candidate/my-profile/bank-account');
          } else if(stepCompletion?.emergency_info === 0){
            navigate('/candidate/my-profile/next-of-kin');
          }
        }
      }
    }

    calculateStageCompletionPercentage(activeStage || "stage1");

    sessionStorage.setItem(
      "stage",
      activeStage !== null ? activeStage : "stage1"
    );
  }, [activeStage, stepCompletion, navigateTo, navigate, location, isStageOneReopened, isStageTwoReopened, isStageThreeReopened, reopenedSections, calculateStageCompletionPercentage, effectiveCurrentStage]);

  const handleStageClick = (stageId: string) => {
    const stageNum = parseInt(stageId.replace('stage', ''));
    if (isStageReopenedFn(stageNum) || stageNum === effectiveCurrentStage) {
      setActiveStage(stageId);
      setNavigateTo("");
    }
    calculateStageCompletionPercentage(stageId);
  };

  const isStageOneApproved = (stageNumber: number) => {
    if (stageNumber) {
      const candidateStageData =
        candidateProfileStages &&
        candidateProfileStages.length > 0 &&
        candidateProfileStages.find(
          (stage: TCandidateProfileStesps) => stage.step_number === stageNumber
        );
      if (
        candidateStageData &&
        (candidateStageData.action === STAGE_1_APPROVE_VALUE ||
          candidateStageData.action === STAGE_1_APPROVE_COMPLIANCE_CHECK_VALUE)
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isStageTwoApproved = (stageNumber: number) => {
    if (stageNumber) {
      const candidateStageData =
        candidateProfileStages &&
        candidateProfileStages.length > 0 &&
        candidateProfileStages.find(
          (stage: TCandidateProfileStesps) => stage.step_number === stageNumber
        );
      if (
        candidateStageData &&
        candidateStageData.action === STAGE_2_APPROVE_VALUE
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isStageThreeApproved = (stageNumber: number) => {
    if (stageNumber) {
      const candidateStageData =
        candidateProfileStages &&
        candidateProfileStages.length > 0 &&
        candidateProfileStages.find(
          (stage: TCandidateProfileStesps) => stage.step_number === stageNumber
        );
      if (
        candidateStageData &&
        candidateStageData.action === STAGE_APPROVED_VALUE
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  useEffect(() => {
    const missing = computeMissingDocs(
      qualifications,
      profileData?.working_rights,
      qualifications?.documents
    );
    setMissingDocs(missing);
  }, [qualifications, profileData]);

  // Get the list of reopened stage names for display
  const getReopenedStageNames = () => {
    return [...reopenedStageNumbers].sort((a, b) => a - b).map(num => {
      if (num === 1) return "Stage 1: Personal Details & Interview";
      if (num === 2) return "Stage 2: Compliance Checks";
      if (num === 3) return "Stage 3: Documents & Contract";
      return `Stage ${num}`;
    });
  };

  useEffect(() => {
    if(currentStage) {
      setCurrentStageName(`stage${currentStage}`);
    }
  },[currentStage])

  const profileSteps = candidateProfileStages || [];
  const isAdminCompleteSelected = profileSteps?.find(
    (step) => step.action === "approve_candidate_bypass_compliance_check"
  )
  const isAdminCompleteApproved = profileSteps?.find(
    (step) => step.action === "approve_candidate_to_stage_3"
  );

  const isAdminCompleted = isAdminCompleteSelected && isAdminCompleteApproved;

  return (
      <div className="d-flex flex-column gap-25 max-w-960px flk-form">
        <StageNotificationBanner
          reopenedStageNumbers={reopenedStageNumbers}
          getReopenedStageNames={getReopenedStageNames}
          activeStage={currentStageName}
          isStageOneReopened={isStageOneReopened}
          isStageTwoReopened={isStageTwoReopened}
          isStageThreeReopened={isStageThreeReopened}
          reviewStatus={profileData?.review_status}
        />

        {/* ProgressBar */}
        {activeStage === "stage1" && (isStageOneReopened() || effectiveCurrentStage === 1) ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-grey-medium">
              {stage1CompletionPercentage}% of Stage 1 completed
            </div>
            <ProgressBar
              progress={stage1CompletionPercentage}
              bgColor="bg-off-white"
              progressColor="bg-teal"
            />
          </div>
        ) : activeStage === "stage2" && (isStageTwoReopened() || effectiveCurrentStage === 2) ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-grey-medium">
              {stage2CompletionPercentage}% of Stage 2 completed
            </div>
            <ProgressBar
              progress={stage2CompletionPercentage}
              bgColor="bg-off-white"
              progressColor="bg-teal"
            />
          </div>
        ) : activeStage === "stage3" && (isStageThreeReopened() || effectiveCurrentStage === 3) ? (
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-grey-medium">
              {stage3CompletionPercentage}% of Stage 3 completed
            </div>
            <ProgressBar
              progress={stage3CompletionPercentage}
              bgColor="bg-off-white"
              progressColor="bg-teal"
            />
          </div>
        ) : (
          ""
        )}

        <div className="d-flex flex-column flex-lg-row gap-10">
          <div className="onbaording-steps min-w-260px d-flex flex-column gap-20">
            {/* Stage 1 */}
            <div className="d-flex flex-column gap-20">
              <div
                className={`onboarding-stage d-flex flex-column gap-8 ${(isStageOneReopened() || effectiveCurrentStage === 1) ? 'cursor-pointer' : ''}`}
                onClick={() => (isStageOneReopened() || effectiveCurrentStage === 1) ? handleStageClick("stage1") : undefined}
              >
                <div className="d-flex">
                  <div className={`bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px`}>
                    Stage 1
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="d-flex flex-column">
                    <div className={`fs-16 fw-700 text-black lh-1 ${isStageOneReopened() ? 'text-decoration-underline' : ''}`}>
                      Personal Details &amp; Interview                    
                    </div>
                    {
                      isStageOneReopened() ? (
                        <div className="fs-13 text-danger fw-700">Reopened and Pending Submission</div>
                      ) : isStageOneApproved(1) ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed and Approved</div>
                      ) : checkStageSubmittedOrNot(1) ? (
                        <div className="fs-13 text-danger fw-700">Submitted and in Review</div>
                      ): (
                        <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                      )
                    }
                  </div>   
                </div>
              </div>
              {activeStage === "stage1" && (isStageOneReopened() || effectiveCurrentStage === 1) && (
                <div className="progress-container flex-column top-0">
                  {stage1Keys.map((sectionKey, index) => { 
                    const stepNumber = index + 1;
                    const baseCompleted = stepCompletion?.[sectionKey as keyof TCompletionStatus] === 1;
                    const isSectionReopened = stage1ReopenedSections.includes(sectionKey); 
                    const isCompleted =  (
                      sectionKey === 'personal_details'
                        ? baseCompleted && !isPersonalDetailsHaveResume
                        : sectionKey === 'qualification'
                        ? baseCompleted && missingDocs.length === 0
                        : baseCompleted
                    );
                    const isActive = location.pathname === `/candidate/my-profile/${sectionRouteMap[sectionKey]}`;
                    const isAllowed = stage1ReopenedSections.length === 0 || stage1ReopenedSections.includes(sectionKey);

                    const sectionNameMap: Record<string, string> = {
                      'id_verification': 'ID Verification',
                      'personal_details': 'Personal Details',
                      'qualification': 'Qualifications',
                      'jobpreference': 'Job Preferences',
                      'online_interview': 'Online Interview',
                    };

                    const displayName = sectionNameMap[sectionKey] || sectionKey
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                    if (!isAllowed) {
                      return (
                        <div
                          key={sectionKey}
                          className="progress-step disabled-step"
                          data-step={stepNumber}
                        >
                          <span className="d-flex flex-column">
                            <span>{displayName}</span>
                            <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={sectionKey}
                        className={`progress-step ${
                          isCompleted ? "completed" : ""
                        } ${
                          isActive ? "fw-700" : ""
                        }`}
                        data-step={stepNumber}
                        to={sectionRouteMap[sectionKey]}
                        data-discover="true"
                        onClick={() => { setNavigateTo(sectionRouteMap[sectionKey]); }}
                      >
                        <span className="d-flex align-items-center gap-2">
                          {displayName}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Stage 2 */}
            <div className="d-flex flex-column gap-20">
              <div
                className={`onboarding-stage d-flex flex-column gap-8 ${(isStageTwoReopened() || effectiveCurrentStage === 2) ? 'cursor-pointer' : ''}`}
                onClick={() => { if (isStageTwoReopened() || effectiveCurrentStage === 2) { setNavigateTo(""); handleStageClick("stage2"); } }}
              >
                <div className="d-flex">
                  <div className={`bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px`}>
                    Stage 2
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="d-flex flex-column">
                    <div className={`fs-16 fw-700 text-black lh-1 ${isStageTwoReopened() || (checkStageSubmittedOrNot(2) || stage2CompletionPercentage === "100") ? !isAdminCompleted ? 'text-decoration-underline' : '' : ''}`}>
                      Compliance Checks 
                    </div>
                    {
                      isStageTwoReopened() ? (
                        <div className="fs-13 text-danger fw-700">Reopened and Pending Submission</div>
                      ) : isAdminCompleted ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed by Connected Crew Admin</div>
                      ) : isStageTwoApproved(2) ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed and Approved</div>
                      ) : (checkStageSubmittedOrNot(2) || stage2CompletionPercentage === "100") ? (
                        <div className="fs-13 text-danger fw-700">Submitted and in Review</div>
                      ): (
                        <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                      )
                    }
                  </div> 
                </div>
              </div>
              {activeStage === "stage2" && (isStageTwoReopened() || effectiveCurrentStage === 2) && (
                <div className="progress-container top-0">
                  <Link
                    className={`progress-step ${
                      ((stepCompletion?.police_check === 1 || userpersonaldetails?.referoo_status === "complete") && !isStageTwoReopened()) ? "completed" : ""
                    }`}
                    data-step="1"
                    to="complaince-check"
                    data-discover="true"
                  >
                    Police Check
                  </Link>
                  <Link
                    className={`progress-step ${
                      ((stepCompletion?.working_with_children === 1 || userpersonaldetails?.referoo_status === "complete") && !isStageTwoReopened()) ? "completed" : ""
                        ? "completed"
                        : ""
                    }`}
                    data-step="2"
                    to="complaince-check"
                    data-discover="true"
                  >
                    Working With Children
                  </Link>
                  <Link
                    className={`progress-step ${
                      ((stepCompletion?.right_to_work === 1 || userpersonaldetails?.referoo_status === "complete") && !isStageTwoReopened()) ? "completed" : ""
                    }`}
                    data-step="3"
                    to="complaince-check"
                    data-discover="true"
                  >
                    Right To Work
                  </Link>
                  <Link
                    className={`progress-step ${
                      ((stepCompletion?.reference_check === 1 || userpersonaldetails?.referoo_status === "complete") && !isStageTwoReopened()) ? "completed" : ""
                    }`}
                    data-step="4"
                    to="complaince-check"
                    data-discover="true"
                  >
                    Reference Check
                  </Link>
                </div>
              )}
            </div>

            {/* Stage 3 */}
            <div className="d-flex flex-column gap-20">
              <div
                className={`onboarding-stage d-flex flex-column gap-8 ${(isStageThreeReopened() || effectiveCurrentStage === 3) ? 'cursor-pointer' : ''}`}
                onClick={() => (isStageThreeReopened() || effectiveCurrentStage === 3) ? handleStageClick("stage3") : undefined}
              >
                <div className="d-flex">
                  <div className={`bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px`}>
                    Stage 3
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="d-flex flex-column">
                    <div className={`fs-16 fw-700 text-black lh-1 ${isStageThreeReopened() || (checkStageSubmittedOrNot(3) || stage3CompletionPercentage === "100") ? 'text-decoration-underline' : ''}`}>
                      Documents &amp; Contract
                    </div>
                    {
                      isStageThreeReopened() ? (
                        <div className="fs-13 text-danger fw-700">Reopened and Pending Submission</div>
                      ) : isStageThreeApproved(3) ? (
                        <div className="fs-13 text-teal-dark fw-700">Completed and Approved</div>
                      ) : checkStageSubmittedOrNot(3) ? (
                        <div className="fs-13 text-danger fw-700">Submitted and in Review</div>
                      ): (
                        <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                      )
                    }
                  </div>
                </div>
              </div>
              {activeStage === "stage3" && (isStageThreeReopened() || effectiveCurrentStage === 3) && (
                <div className="progress-container flex-column top-0">
                  {stage3Keys.map((sectionKey, index) => {
                    const stepNumber = index + 1;
                    const isSectionReopened = stage3ReopenedSections.includes(sectionKey);
                    const isCompleted = stepCompletion?.[sectionKey as keyof TCompletionStatus] === 1;
                    const isActive = location.pathname === `/candidate/my-profile/${sectionRouteMap[sectionKey]}`;
                    const isAllowed = stage3ReopenedSections.length === 0 || stage3ReopenedSections.includes(sectionKey);

                    const sectionNameMap: Record<string, string> = {
                      'tfn_form': 'TFN Declaration',
                      'superannuation_form': 'Superannuation',
                      'contract_form': 'Contract',
                      'casual_fair_work_statement': 'Casual & Fair Work Statement',
                      'bankAccount': 'Bank Account',
                      'emergency_info': 'Next of Kin',
                    };

                    const displayName = sectionNameMap[sectionKey] || sectionKey
                      .split('_')
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ');

                    if (!isAllowed) {
                      return (
                        <div
                          key={sectionKey}
                          className="progress-step disabled-step"
                          data-step={stepNumber}
                        >
                          <span className="d-flex flex-column">
                            <span>{displayName}</span>
                            <span className="fs-11 fw-400 text-grey-medium">Editing Disabled</span>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={sectionKey}
                        className={`progress-step ${
                          isCompleted ? "completed" : ""
                        } ${
                          isActive ? "fw-700" : ""
                        }`}
                        data-step={stepNumber}
                        to={sectionRouteMap[sectionKey]}
                        data-discover="true"
                      >
                        <span className="d-flex align-items-center gap-2">
                          {displayName}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="border-left border-off-white pl-50px w-100 mobile-p-0 mobile-border-0">
            <Outlet />
          </div>
        </div>
      </div>
  );
};

export default MyProfileReopened;
